import { useEffect, useRef, useState } from "react"
import { Link, Navigate, useParams } from "react-router-dom"
import { Note } from "../components/Note"
import { useStoredNumber } from "../hooks/useStored"
import { StorageKey } from "../lib/storage"
import { GUIDES, SPEEDS, type Guide } from "./guides/guides"

/**
 * Видео-гайды — экран `/guides` и `/guides/<id>`.
 *
 * Плеер — обычный `<video>` со встроенными кнопками браузера. Сверху него
 * только то, чего встроенным не хватает: скорость до 2× одним нажатием (во
 * встроенном меню она спрятана за «⋮») и главы — переход сразу к нужному шагу.
 * Скорость запоминается между роликами и заходами.
 *
 * Без адреса гайда открывается первый из списка; неизвестный адрес ведёт
 * обратно на `/guides`.
 */
export default function GuidesScreen() {
	const { id } = useParams()
	const guide = id ? GUIDES.find(item => item.id === id) : GUIDES[0]
	if (!guide) return <Navigate to="/guides" replace />
	// новый гайд — новый плеер: время и ошибка прошлого ролика к нему не относятся
	return <GuideView key={guide.id} guide={guide} />
}

/** 75 → «1:15». */
const clock = (seconds: number) =>
	`${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`

function GuideView({ guide }: { guide: Guide }) {
	const video = useRef<HTMLVideoElement>(null)
	const [stored, setSpeed] = useStoredNumber(StorageKey.guidesSpeed, 1)
	// в хранилище может лежать что угодно; встроенное меню браузера даёт и 0,25×
	const speed = stored > 0 && stored <= 2 ? stored : 1
	const [time, setTime] = useState(0)
	const [failed, setFailed] = useState(false)

	// defaultPlaybackRate — чтобы скорость пережила перезагрузку источника
	useEffect(() => {
		const node = video.current
		if (!node) return
		node.defaultPlaybackRate = speed
		node.playbackRate = speed
	}, [speed])

	// главы идут по порядку — текущая последняя из начавшихся
	const current = guide.chapters.filter(chapter => chapter.at <= time + 0.25).pop()

	const jump = (at: number) => {
		const node = video.current
		if (!node) return
		node.currentTime = at
		// play() отклоняется, если браузер не дал запуск, — тогда просто стоим на главе
		void node.play().catch(() => undefined)
	}

	return (
		<div className="guides">
			{failed && (
				<Note tone="error">
					Видео не загрузилось: {guide.video}. Если ошибка повторяется —
					файла нет на узле.
				</Note>
			)}

			<div className="guides__player">
				<video
					ref={video}
					className="guides__video"
					src={guide.video}
					poster={guide.poster}
					controls
					// звук по умолчанию выключен (просьба пользователя): включают кнопкой плеера
					muted
					preload="metadata"
					playsInline
					onTimeUpdate={event => setTime(event.currentTarget.currentTime)}
					// скорость, выбранная во встроенном меню браузера, тоже запоминается
					onRateChange={event => {
						const rate = event.currentTarget.playbackRate
						if (rate !== speed) setSpeed(rate)
					}}
					onError={() => setFailed(true)}
				/>
			</div>

			<div className="guides__bar">
				<div>
					<h2 className="guides__title">{guide.title}</h2>
					<div className="muted">{guide.summary}</div>
				</div>
				<div className="guides__speed">
					<span className="muted">Скорость</span>
					<div className="segmented">
						{SPEEDS.map(rate => (
							<button
								key={rate}
								aria-pressed={rate === speed}
								onClick={() => setSpeed(rate)}
							>
								{rate.toLocaleString("ru")}×
							</button>
						))}
					</div>
				</div>
			</div>

			<div className="guides__chapters">
				{guide.chapters.map(chapter => (
					<button
						key={chapter.at}
						className="guides__chapter"
						aria-current={chapter === current}
						onClick={() => jump(chapter.at)}
					>
						<span className="mono">{clock(chapter.at)}</span>
						{chapter.title}
					</button>
				))}
			</div>

			<Link className="guides__screen" to={guide.screen.to}>
				Открыть экран «{guide.screen.label}» →
			</Link>

			<div className="guides__list-head">Все гайды · {GUIDES.length}</div>
			<div className="guides__list">
				{GUIDES.map(item => (
					<Link
						key={item.id}
						to={`/guides/${item.id}`}
						className={
							"guides__item" + (item.id === guide.id ? " guides__item--active" : "")
						}
					>
						<img src={item.poster} alt="" loading="lazy" />
						<div>
							<b>{item.title}</b>
							<span className="muted">{item.duration}</span>
						</div>
					</Link>
				))}
			</div>
		</div>
	)
}
